import { useState } from "react";

export default function ImportInventoryModal({ onClose, onImported }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const downloadTemplate = () => {
    window.open("/api/inventory/import-template", "_blank");
  };

  const importExcel = async () => {
    if (!file) {
      setError("Select an Excel file first.");
      return;
    }

    setUploading(true);
    setError("");
    setResult(null);

    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await fetch(
        "/api/inventory/import-excel",
        {
          method: "POST",
          credentials: "include",
          body: formData,
        },
      );

      const data = await response.json();

      if (data.status === "success") {
        setResult(data);
        await onImported();
      } else {
        setError(data.message || "Import failed.");
      }
    } catch (err) {
      console.error(err);
      setError("Could not import the Excel file.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Modal onClose={onClose}>
      <h2 className="text-xl font-serif">Import Inventory from Excel</h2>

      <p className="mt-2 text-sm text-gray-400">
        Download the template, fill it, then upload the completed .xlsx file.
        Existing item names in the same branch are skipped.
      </p>

      <button
        onClick={downloadTemplate}
        className="mt-5 rounded-xl border border-amber-400/50 px-4 py-2 text-sm text-amber-400"
      >
        Download Excel Template
      </button>

      <input
        type="file"
        accept=".xlsx"
        onChange={(e) => setFile(e.target.files?.[0] || null)}
        className="mt-5 block w-full rounded-lg border border-white/10 bg-black/40 p-3 text-sm"
      />

      {error && <p className="mt-4 text-sm text-red-400">{error}</p>}

      {result && (
        <div className="mt-5 rounded-xl border border-white/10 bg-black/30 p-4 text-sm">
          <p className="text-emerald-400">Created: {result.created}</p>

          <p className="mt-1 text-amber-400">Skipped: {result.skipped}</p>

          {result.errors?.length > 0 && (
            <div className="mt-3 max-h-32 overflow-y-auto text-gray-400">
              {result.errors.map((item) => (
                <p key={`${item.row}-${item.message}`}>
                  Row {item.row}: {item.message}
                </p>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="mt-8 flex justify-end gap-4">
        <button
          onClick={onClose}
          className="rounded-xl border border-white/20 px-6 py-2"
        >
          Close
        </button>

        <button
          onClick={importExcel}
          disabled={uploading}
          className="rounded-xl bg-amber-400 px-6 py-2 font-medium text-black disabled:opacity-50"
        >
          {uploading ? "Importing..." : "Import Inventory"}
        </button>
      </div>
    </Modal>
  );
}

function Modal({ children, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="relative w-full max-w-xl rounded-2xl border border-white/10 bg-[#0b0b0f] p-8">
        <button
          onClick={onClose}
          className="absolute right-4 top-4 text-gray-400 hover:text-white"
        >
          ✕
        </button>

        {children}
      </div>
    </div>
  );
}
